import { types } from 'features/teamlist/store/types'
import { createAction } from 'redux-actions'
import { constants, utils } from 'gipsy-misc'
import { cancelled, teamlist } from 'gipsy-api'

import logger from 'utils/logger'
import { groupTeamItems } from 'features/teamlist/utils'
import { scrollElementId } from 'features/teamlist/components/members'
import { closePopup } from 'features/popup/store/actions'
import { handleAPIError } from 'features/app/store/actions'
import { progressPending, progressSuccess, progressError } from 'features/progressBar/store/actions'

import { moveElementInItemsfromPositionToPosition } from 'features/todolist/utils'
import { findElementInItemsByPosition } from 'features/list/utils'
import { updateTaskPartiallyById } from 'features/task/store/actions'

export const addArchiveItems = createAction(types.ADD_ARCHIVE_ITEMS)
export const _activateTeamMember = createAction(types.ACTIVATE_TEAM_MEMBER)
export const setTeamMembers = createAction(types.SET_TEAMMEMBERS)
export const setTeamItems = createAction(types.SET_TEAM_ITEMS)
export const setTeamItemsForGroup = createAction(types.SET_TEAM_ITEMS_FOR_GROUP)
export const setItemsLoaded = createAction(types.SET_ITEMS_LOADED)

export const onAddUsersFromPopup = users => async (dispatch, getState, { api }) => {
  dispatch(closePopup())
  if (users.length === 0) {
    return
  }
  try {
    /* should scroll to addTeamMembers[0].id */
    const addedUsers = await teamlist.addTeamMembers(users)
    await dispatch(getTeamMembers())
    if (addedUsers.length > 0) {
      const userId = utils.user.getKey(addedUsers[0])
      dispatch(activateTeamMember(addedUsers[0]))
      utils.DOM.scrollWhenExistsToElementWithId(userId, scrollElementId)
    }
  } catch (err) {
    console.error(err)
  }
}

export const activateTeamMember = user => async (dispatch, getState, { api }) => {
  if (utils.user.areSameUser(user, getState().teamlist.activeTeamMember)) {
    return
  }
  dispatch(_activateTeamMember(user))
}

export const load = routeUser => async (dispatch, getState, { api }) => {
  await dispatch(getTeamMembers(routeUser))
}

export const getTeamMembers = routeUser => async (dispatch, getState, { api }) => {
  const currentTeamMembers = getState().teamlist.teamMembers
  await dispatch(activateTeamMemberIfExists(routeUser, currentTeamMembers))

  try {
    const teamMembers = (await api.teamlist.getTeamMembers()) || []
    await dispatch(activateTeamMemberIfExists(routeUser, teamMembers))
    dispatch(setTeamMembers(teamMembers))
  } catch (err) {
    logger.error(err)
  }
}

const activateTeamMemberIfExists = (user, teamMembers) => async dispatch => {
  if (!user || !teamMembers || !teamMembers.length) {
    return
  }
  const routerUserIndexInTeamMembers = utils.array.getObjectIndexInArrayMatchingAtLeastOneField(teamMembers, user, [
    'userId',
    'email'
  ])
  if (routerUserIndexInTeamMembers === -1) {
    return
  }
  const activeUser = teamMembers[routerUserIndexInTeamMembers]
  await dispatch(activateTeamMember(activeUser))
}

/* fetch todolist without any delay */
export const getTeamListWithoutDelay = (assignee = null) => async (dispatch, getState, { api }) => {
  assignee = assignee || getState().teamlist.activeTeamMember
  try {
    dispatch(cancelGetTeamlist())
    const data = (await api.teamlist.get(assignee)) || {}
    dispatch(handleTeamListData(data))
  } catch (err) {
    if (!err.cancel && !cancelled.isCancelled(err)) {
      logger.error(err)
    }
  }
}

export const cancelGetTeamlist = () => async (dispatch, getState, { api }) => {
  utils.resolve.cancel('teamlist')
  api.teamlist.cancel('get')
}

/* fetch todolist with 1sec delay and send the request only once, each call of this function will reset the 1s delay */
export const getTeamList = (assignee = null) => async (dispatch, getState, { api }) => {
  assignee = assignee || getState().teamlist.activeTeamMember
  try {
    const data = (await utils.resolve.awaitOnce('teamlist', api.teamlist.get.bind(null, assignee))) || {}
    dispatch(handleTeamListData(data))
  } catch (err) {
    if (!err.cancel && !cancelled.isCancelled(err)) {
      logger.error(err)
    }
  }
}

export const handleTeamListData = data => async (dispatch, getState, { api }) => {
  dispatch(setTeamItems(groupTeamItems(data)))
  dispatch(setItemsLoaded(true))
}

/* fetch todolist without any delay */
export const getArchiveTeamListWithoutDelay = (
  assignee = null,
  lastSortKey = '',
  limit = constants.limitPaginationArchive
) => async (dispatch, getState, { api }) => {
  assignee = assignee || getState().teamlist.activeTeamMember
  try {
    const data = (await api.teamlist.getArchive(assignee, lastSortKey, limit)) || []
    dispatch(handleArchiveTeamListData(data, lastSortKey))
  } catch (err) {
    logger.error(err)
  }
}

/* fetch todolist with 1sec delay and send the request only once, each call of this function will reset the 1s delay */
export const getArchiveTeamList = (
  assignee = null,
  lastSortKey = '',
  limit = constants.limitPaginationArchive
) => async (dispatch, getState, { api }) => {
  assignee = assignee || getState().teamlist.activeTeamMember
  try {
    const data =
      (await utils.resolve.awaitOnce(
        `archive-teamlist-${lastSortKey}`,
        api.teamlist.getArchive.bind(null, assignee, lastSortKey, limit)
      )) || []
    dispatch(handleArchiveTeamListData(data, lastSortKey))
  } catch (err) {
    if (!err.cancel) {
      logger.error(err)
    }
  }
}

export const handleArchiveTeamListData = (data, lastSortKey) => async (dispatch, getState, { api }) => {
  if (lastSortKey) {
    if (data && data.length) {
      dispatch(addArchiveItems(data))
    }
    return
  }
  dispatch(setTeamItems({ archive: { list: data } }))
}

export const removeTeamMember = teamMember => async (dispatch, getState, { api }) => {
  dispatch(progressPending())

  let teamMembers = getState().teamlist.teamMembers
  let index = 0
  let newTeamMembers
  for (let member of teamMembers) {
    if (utils.user.areSameUser(member, teamMember)) {
      newTeamMembers = teamMembers.slice(0, index).concat(teamMembers.slice(index + 1))
      break
    }
    index++
  }
  dispatch(setTeamMembers(newTeamMembers))

  try {
    await teamlist.removeTeamMembers(teamMember)
    dispatch(progressSuccess())
  } catch (err) {
    dispatch(progressError())
    dispatch(handleAPIError(err))
  }
  dispatch(getTeamMembers())
}

/* from / to should be an object referencing the table and index in this table { table : 0, index: 0 } */
export const dragAndDrop = ({ id, from, to }) => (dispatch, getState, { api }) => {
  dispatch(cancelGetTeamlist())
  if (from.table === to.table && from.index === to.index) {
    return
  }
  return new Promise((resolve, reject) => {
    /* prevent to show the invalid date label when dropped into upcoming */
    if (from.table !== to.table && utils.datetime.isGroupUpcoming(to.table)) {
      dispatch(updateTaskPartiallyById(id, { when: { date: null } }))
    }

    const teamItems = getState().teamlist.items
    const teamItemToMove = findElementInItemsByPosition(teamItems, {
      group: from.table,
      index: from.index
    })
    let newTeamItems = moveElementInItemsfromPositionToPosition(
      teamItemToMove,
      teamItems,
      { group: from.table, index: from.index },
      { group: to.table, index: to.index }
    )
    dispatch(setTeamItems(newTeamItems))
    resolve()

    const dragAndDropUpdate = utils.list.buildDragAndDropUpdateBody(to.table, to.index, newTeamItems[to.table].list)
    if (!dragAndDropUpdate) {
      dispatch(
        handleAPIError("Couldn't build dragAndDropUpdate body for request", {
          teamItems,
          from,
          to
        })
      )
      return
    }
    api.task
      .dragAndDropForTeam(dragAndDropUpdate)
      .then(response => {
        dispatch(getTeamList())
      })
      .catch(err => {
        dispatch(handleAPIError(err, { dragAndDropUpdate, from, to }))
      })
  })
}



// WEBPACK FOOTER //
// ./src/features/teamlist/store/actions.js